// consent.js — MODULE 3: Consent Banner + PostHog init dispatch
//
// Cookie:   probabl_consent = 'accepted' | 'rejected'
// Domain:   pbConfig.cookie_domain (e.g. ".probabl.ai")
// Lifetime: 90 days
//
// On page load:
//   accepted cookie → loadPostHog('accepted') + gtag consent update + Reo
//   rejected cookie → loadPostHog('rejected')
//   no cookie       → show banner; on click → set cookie + same as above
//   no_track host   → nothing loaded (PostHog stub not installed either)

import { cfg, LOG, dbg }    from './config.js';
import { loadPostHog }      from './posthog.js';
import BANNER_CSS           from './banner.css';
import BANNER_CSS_LIGHT     from './banner-light.css';
import BANNER_HTML          from './banner.html';

try {
  var CONSENT_COOKIE = 'probabl_consent';
  var CONSENT_DAYS   = 90;
  var cookieDomain   = cfg.cookie_domain;

  function pbSetCookie(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + d.toUTCString();
    }
    var domain = cookieDomain ? '; domain=' + cookieDomain : '';
    var secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=' + (value || '') + expires + '; path=/' + domain + '; SameSite=Lax' + secure;
  }

  function pbGetCookie(name) {
    var nameEQ = name + '=';
    var parts  = document.cookie.split(';');
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i];
      while (c.charAt(0) === ' ') { c = c.substring(1); }
      if (c.indexOf(nameEQ) === 0) { return c.substring(nameEQ.length); }
    }
    return null;
  }

  // source: 'page_load' | 'banner_click'
  function onAccept(source) {
    loadPostHog('accepted');

    // gtag stub may already exist (from gtm.js). Define it as a fallback
    // so the consent update never throws.
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function() { window.dataLayer.push(arguments); };
    }
    try {
      window.gtag('consent', 'update', {
        ad_storage:         'granted',
        analytics_storage:  'granted',
        ad_user_data:       'granted',
        ad_personalization: 'granted'
      });
    } catch (e) { dbg('gtag consent update error', e); }

    // GTM tags listen for consent_confirmed (e.g. LinkedIn Insight).
    try {
      window.dataLayer.push({
        event:          'consent_confirmed',
        consent_source: source
      });
    } catch (e) { dbg('dataLayer consent_confirmed error', e); }

    // Reo.dev — load once on first consent grant.
    if (cfg.reo_id && !window.Reo) {
      try {
        var reoScript   = document.createElement('script');
        reoScript.src   = 'https://static.reo.dev/' + cfg.reo_id + '/reo.js';
        reoScript.defer = true;
        reoScript.onload = function() {
          try {
            if (window.Reo) { window.Reo.init({ clientID: cfg.reo_id }); }
          } catch (e) { dbg('Reo.init error', e); }
        };
        document.head.appendChild(reoScript);
        dbg('Reo.dev loading');
      } catch (e) { dbg('Reo loading error', e); }
    }

    dbg('Consent granted (' + source + ')');
  }

  function onReject() {
    loadPostHog('rejected');
    window.dataLayer = window.dataLayer || [];
    try {
      window.dataLayer.push({ event: 'consent_rejected' });
    } catch (e) { dbg('dataLayer consent_rejected error', e); }
    dbg('Consent denied');
  }

  function injectBanner() {
    var style = document.createElement('style');
    style.textContent = cfg.banner_theme === 'default' ? BANNER_CSS : BANNER_CSS_LIGHT;
    document.head.appendChild(style);

    var tmp = document.createElement('div');
    tmp.innerHTML = BANNER_HTML;
    document.body.appendChild(tmp.firstElementChild);
  }

  function showBanner() {
    setTimeout(function() {
      var w = document.getElementById('cb-banner-wrapper');
      if (w) { w.classList.add('cb-visible'); }
      var rj = document.getElementById('cb-reject');
      if (rj) { rj.focus(); }
    }, 10);
  }

  function hideBanner() {
    var w = document.getElementById('cb-banner-wrapper');
    if (w) { w.classList.remove('cb-visible'); }
  }

  function attachBannerListeners() {
    var acceptBtn = document.getElementById('cb-accept');
    var rejectBtn = document.getElementById('cb-reject');

    if (acceptBtn) {
      acceptBtn.onclick = function() {
        pbSetCookie(CONSENT_COOKIE, 'accepted', CONSENT_DAYS);
        hideBanner();
        onAccept('banner_click');
      };
    }

    if (rejectBtn) {
      rejectBtn.onclick = function() {
        pbSetCookie(CONSENT_COOKIE, 'rejected', CONSENT_DAYS);
        hideBanner();
        onReject();
      };
    }
  }

  function initConsent() {
    if (cfg.no_track === true) {
      dbg('no_track host — nothing loaded');
      return;
    }

    var consent = pbGetCookie(CONSENT_COOKIE);

    if (consent === 'accepted') {
      onAccept('page_load');
      return;
    }

    if (consent === 'rejected') {
      onReject();
      return;
    }

    // Cookie absent — first visit, show banner.
    if (document.body) {
      injectBanner();
      showBanner();
      attachBannerListeners();
    } else {
      document.addEventListener('DOMContentLoaded', function() {
        injectBanner();
        showBanner();
        attachBannerListeners();
      });
    }
  }

  initConsent();

} catch (e) {
  console.error(LOG, 'Consent banner error', e);
}
